type RecordLike = Record<string, unknown>;

export type AcCheckInputItem = {
  token: string;
  source: string;
};

export type AcCheckResultItem = {
  token: string;
  email: string;
  status: string;
  tokenOk: boolean;
  eligible: boolean;
  couponState: string;
  plan: string;
  httpStatus: string;
  reason: string;
  jwtExpired: boolean;
  expiresAt: string;
  jwtLabel: string;
  source: string;
  raw: RecordLike;
};

export type AcCheckSummary = {
  total: number;
  eligible: number;
  ineligible: number;
  expired: number;
  tokenOk: number;
  couponEligible: number;
};

const TOKEN_RE = /eyJ[0-9A-Za-z_-]{20,}\.[0-9A-Za-z_-]{10,}\.[0-9A-Za-z_-]{10,}/g;
const ACCESS_TOKEN_RE = /"?(?:accessToken|access_token)"?\s*:\s*"([^"]+)"/g;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

function isRecord(value: unknown): value is RecordLike {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function rowString(row: RecordLike, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function rowBoolean(row: RecordLike, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return true;
      if (value.toLowerCase() === "false") return false;
    }
  }
  return false;
}

function rowNumber(row: RecordLike, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

function formatDays(value: number) {
  if (!Number.isFinite(value)) return "";
  if (value >= 1) return `${Math.round(value)}d后`;
  const hours = Math.max(1, Math.round(value * 24));
  return `${hours}h后`;
}

function extractEmail(row: RecordLike, text: string) {
  return rowString(row, ["email", "account_email", "user_email"]) || text.match(EMAIL_RE)?.[0] || "";
}

function extractTokenText(input: string) {
  const text = input.trim();
  if (!text) return [];

  const tokens = new Set<string>();
  const jsonChunks = text.match(/\{[\s\S]*?\}/g) || [];

  for (const chunk of jsonChunks) {
    try {
      const parsed = JSON.parse(chunk);
      const stack: unknown[] = [parsed];
      while (stack.length) {
        const current = stack.pop();
        if (Array.isArray(current)) {
          stack.push(...current);
          continue;
        }
        if (!isRecord(current)) continue;
        const token = rowString(current, ["accessToken", "access_token", "token"]);
        if (token) tokens.add(token);
        for (const value of Object.values(current)) stack.push(value);
      }
    } catch {
      // ignore malformed embedded JSON and continue with regex extraction
    }
  }

  for (const match of text.matchAll(ACCESS_TOKEN_RE)) {
    tokens.add(match[1].trim());
  }
  for (const match of text.matchAll(TOKEN_RE)) {
    tokens.add(match[0].trim());
  }

  if (!tokens.size && text.includes("\n")) {
    for (const line of text.split(/\n+/)) {
      const trimmed = line.trim();
      if (trimmed) tokens.add(trimmed);
    }
  }

  return Array.from(tokens).filter(Boolean).slice(0, 50);
}

export function parseAcCheckInput(input: string) {
  const tokens = extractTokenText(input);
  return tokens.map<AcCheckInputItem>((token, index) => ({ token, source: `token-${index + 1}` }));
}

export function normalizeAccessToken(value: string) {
  const text = value.trim();
  if (!text || !text.startsWith("{")) return text;
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return text;
    const row = parsed as Record<string, unknown>;
    const token = row.accessToken ?? row.access_token;
    return typeof token === "string" ? token.trim() : text;
  } catch {
    return text;
  }
}

export function parseAcCheckResponse(data: unknown): AcCheckResultItem[] {
  if (!data || typeof data !== "object") return [];
  const root = data as RecordLike;
  const nested = isRecord(root.data) ? root.data : root;
  const items = Array.isArray(nested.items) ? nested.items : Array.isArray(root.items) ? root.items : [];
  if (!items.length && isRecord(nested.result)) {
    const result = nested.result;
    return [normalizeAcCheckRow(result, "result-1", String(root.source || "result-1"))];
  }
  if (!items.length && (rowHasCheckFields(root) || rowHasCheckFields(nested))) {
    return [normalizeAcCheckRow(isRecord(root.data) ? (root.data as RecordLike) : root, "result-1", "result-1")];
  }
  return items
    .map((item, index) => (isRecord(item) ? normalizeAcCheckRow(item, `item-${index + 1}`, String(root.source || `item-${index + 1}`)) : null))
    .filter((item): item is AcCheckResultItem => Boolean(item));
}

function rowHasCheckFields(row: RecordLike) {
  return ["token_ok", "eligible", "reason", "coupon_state", "jwt_expired", "message", "error", "status"].some((key) => key in row);
}

function normalizeAcCheckRow(row: RecordLike, fallbackToken: string, source: string): AcCheckResultItem {
  const token = rowString(row, ["token", "accessToken", "access_token"]) || fallbackToken;
  const email = extractEmail(row, JSON.stringify(row));
  const status = rowString(row, ["status", "display_status"]) || (rowBoolean(row, ["eligible"]) ? "eligible" : "unknown");
  const tokenOk = rowBoolean(row, ["token_ok", "tokenOk", "ok"]);
  const eligible = rowBoolean(row, ["eligible", "is_eligible"]);
  const couponState = rowString(row, ["coupon_state", "couponState"]);
  const plan = rowString(row, ["plan", "plan_type", "subscription_plan"]);
  const httpStatus = rowString(row, ["http_status", "httpStatus", "status_code"]);
  const reason = rowString(row, ["reason", "message", "error_message", "error"]);
  const jwtExpired = rowBoolean(row, ["jwt_expired", "jwtExpired", "expired"]);
  const expiresAt = rowString(row, ["expires_at", "expire_at", "jwt_exp"]);
  const expiresIn = rowNumber(row, ["expires_in_days", "expires_in_day", "days_left", "expires_in", "expire_in"]);
  const jwtLabel = expiresAt || formatDays(expiresIn ?? NaN);

  return {
    token,
    email,
    status,
    tokenOk,
    eligible,
    couponState,
    plan,
    httpStatus,
    reason,
    jwtExpired,
    expiresAt,
    jwtLabel,
    source,
    raw: row
  };
}

export function summarizeAcCheckResults(items: AcCheckResultItem[]): AcCheckSummary {
  return items.reduce<AcCheckSummary>(
    (acc, item) => {
      acc.total += 1;
      if (item.tokenOk) acc.tokenOk += 1;
      if (item.eligible) acc.eligible += 1;
      if (!item.eligible) acc.ineligible += 1;
      if (item.jwtExpired) acc.expired += 1;
      if (item.couponState === "eligible") acc.couponEligible += 1;
      return acc;
    },
    { total: 0, eligible: 0, ineligible: 0, expired: 0, tokenOk: 0, couponEligible: 0 }
  );
}

export function acCheckStatusLabel(item: AcCheckResultItem) {
  if (item.jwtExpired) return "JWT 过期";
  if (!item.tokenOk) return "无效 token";
  if (item.eligible) return "有资格";
  if (item.couponState === "eligible") return "可领取";
  if (item.status === "not_eligible") return "无资格";
  return item.status || "待确认";
}

export function acCheckBadgeClass(item: AcCheckResultItem) {
  if (item.jwtExpired) return "bad";
  if (!item.tokenOk) return "bad";
  if (item.eligible) return "success";
  if (item.couponState === "eligible") return "warning";
  return "neutral";
}

export function acCheckDetailText(item: AcCheckResultItem) {
  const pieces = [
    item.reason || acCheckStatusLabel(item),
    item.couponState ? `coupon=${item.couponState}` : "",
    item.plan ? `plan=${item.plan}` : "",
    item.httpStatus ? `http=${item.httpStatus}` : "",
    item.status ? `status=${item.status}` : "",
    item.expiresAt ? `jwt=${item.expiresAt}` : "",
    !item.expiresAt && item.jwtLabel ? `jwt=${item.jwtLabel}` : ""
  ].filter(Boolean);
  return pieces.join(" · ");
}

export function acCheckCsv(items: AcCheckResultItem[]) {
  const header = ["token", "email", "status", "token_ok", "eligible", "coupon_state", "reason", "jwt_expired", "expires_at", "source"];
  const rows = items.map((item) =>
    [
      item.token,
      item.email,
      item.status,
      String(item.tokenOk),
      String(item.eligible),
      item.couponState,
      item.reason,
      String(item.jwtExpired),
      item.expiresAt,
      item.source
    ]
      .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
      .join(",")
  );
  return [header.join(","), ...rows].join("\n");
}
